import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';

/**
 * Helper to extract page count from DOCX or PDF
 */
const getPageCount = async (zip, path) => {
  const ext = path.split('.').pop().toLowerCase();
  try {
    const data = await zip.file(path).async("arraybuffer");
    if (ext === 'docx') {
      const docx = await JSZip.loadAsync(data);
      const appXml = await docx.file("docProps/app.xml")?.async("text");
      if (appXml) {
        const match = appXml.match(/<Pages>(\d+)<\/Pages>/);
        return match ? parseInt(match[1], 10) : 1;
      }
    } else if (ext === 'pdf') {
      const pdfDoc = await PDFDocument.load(data, { ignoreEncryption: true });
      return pdfDoc.getPageCount();
    }
  } catch (e) {
    console.warn(`Could not count pages for ${path}:`, e);
  }
  return 1;
};

const getFilePrefix = (filename) => {
  const parts = filename.split('_');
  return parts.length > 1 ? parts[0] : filename;
};

/**
 * Extracts a clean Master Name from a filename like "2 Architectural.docx" -> "Architectural"
 */
const cleanMasterName = (filename) => {
  let name = filename.replace(/\.[^/.]+$/, "");
  // Remove leading digit and any following punctuation/space
  name = name.replace(/^\d+[\s\-_]*/, "");
  return name.trim();
};

/**
 * Extract numerical index from string (e.g., "Appendix 1" -> "1", "2 Architectural.docx" -> "2")
 */
const getAppNumber = (str) => {
  if (!str) return null;
  const match = str.match(/\d+/);
  return match ? match[0] : null;
};

const generateGlobalTabMap = async (mainZip, allPaths, onLog) => {
  const globalMapping = {}; 
  const appendixGroups = {};
  const appendixIndexToName = {}; // Maps "2" -> "Architectural"
  
  const PAGE_LIMIT = 50;
  const SET_LIMIT = 6;

  // First pass: Identify Master Names from ROOT files
  allPaths.forEach(path => {
    const parts = path.split('/');
    const filename = parts[parts.length - 1];
    
    // If file is at the root level (parts.length === 1)
    if (parts.length === 1 && filename.toLowerCase().endsWith('.docx') && !filename.startsWith('~$')) {
      const num = getAppNumber(filename);
      if (num) {
        const name = cleanMasterName(filename);
        appendixIndexToName[num] = name;
        onLog(`Found Master Doc for Index ${num}: "${name}"`);
      }
    }
  });

  // Second pass: Group files inside Appendices and assign Tabs
  allPaths.forEach(path => {
    const parts = path.split('/');
    // We only create tabs for files inside "Appendix X" folders
    if (parts.length >= 2 && parts[0].toLowerCase().startsWith('appendix')) {
      const appendixRoot = parts[0];
      const folderPathParts = parts.slice(1, -1);
      
      if (folderPathParts.length > 0) {
        const fullFolderPath = folderPathParts.join('/');
        if (!appendixGroups[appendixRoot]) appendixGroups[appendixRoot] = {};
        if (!appendixGroups[appendixRoot][fullFolderPath]) {
          appendixGroups[appendixRoot][fullFolderPath] = [];
        }
        appendixGroups[appendixRoot][fullFolderPath].push(path);
      }
    }
  });

  for (const [appendix, folders] of Object.entries(appendixGroups)) {
    let currentTabIdx = 1;
    const sortedFolderPaths = Object.keys(folders).sort((a, b) => 
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );

    for (const folderPath of sortedFolderPaths) {
      const files = folders[folderPath].sort((a, b) => 
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );

      let currentTabPageCount = 0;
      let currentTabPrefixes = new Set();
      let lastPrefix = null;
      
      onLog(`Analyzing pages in ${folderPath}...`);

      for (let i = 0; i < files.length; i++) {
        const filePath = files[i];
        const filename = filePath.split('/').pop();
        const pages = await getPageCount(mainZip, filePath);
        const currentPrefix = getFilePrefix(filename);

        if (lastPrefix !== null && currentPrefix !== lastPrefix) {
          if (currentTabPageCount >= PAGE_LIMIT || currentTabPrefixes.size >= SET_LIMIT) {
            currentTabIdx++;
            currentTabPageCount = 0;
            currentTabPrefixes = new Set();
          }
        }

        currentTabPrefixes.add(currentPrefix);
        const tabName = `Tab ${String(currentTabIdx).padStart(2, '0')}`;
        
        globalMapping[filePath] = {
          tabName,
          originalPath: folderPath,
          pages: pages
        };

        currentTabPageCount += pages;
        lastPrefix = currentPrefix;
      }
      currentTabIdx++;
    }
  }

  return { globalMapping, appendixIndexToName };
};

const createWordTable = (docXml, ns, appendixName, fileToTabMap) => {
  const tbl = docXml.createElementNS(ns, "w:tbl");
  const tblPr = docXml.createElementNS(ns, "w:tblPr");
  const tblBorders = docXml.createElementNS(ns, "w:tblBorders");
  ['top', 'left', 'bottom', 'right', 'insideH', 'insideV'].forEach(side => {
    const border = docXml.createElementNS(ns, `w:${side}`);
    border.setAttribute("w:val", "single");
    border.setAttribute("w:sz", "4");
    border.setAttribute("w:space", "0");
    border.setAttribute("w:color", "auto");
    tblBorders.appendChild(border);
  });
  tblPr.appendChild(tblBorders);
  tbl.appendChild(tblPr);

  const headerRow = docXml.createElementNS(ns, "w:tr");
  ['TAB', 'FILE NAME'].forEach(text => {
    const tc = docXml.createElementNS(ns, "w:tc");
    const p = docXml.createElementNS(ns, "w:p");
    const r = docXml.createElementNS(ns, "w:r");
    const rPr = docXml.createElementNS(ns, "w:rPr");
    rPr.appendChild(docXml.createElementNS(ns, "w:b"));
    const t = docXml.createElementNS(ns, "w:t");
    t.textContent = text;
    r.appendChild(rPr);
    r.appendChild(t);
    p.appendChild(r);
    tc.appendChild(p);
    headerRow.appendChild(tc);
  });
  tbl.appendChild(headerRow);

  const appendixFiles = Object.entries(fileToTabMap)
    .filter(([path]) => path.startsWith(appendixName))
    .sort((a, b) => a[1].tabName.localeCompare(b[1].tabName));

  const tabGroups = {};
  appendixFiles.forEach(([path, mapping]) => {
    if (!tabGroups[mapping.tabName]) tabGroups[mapping.tabName] = [];
    tabGroups[mapping.tabName].push(path.split('/').pop());
  });

  Object.entries(tabGroups).forEach(([tab, files]) => {
    const tr = docXml.createElementNS(ns, "w:tr");
    const tc1 = docXml.createElementNS(ns, "w:tc");
    const p1 = docXml.createElementNS(ns, "w:p");
    const r1 = docXml.createElementNS(ns, "w:r");
    const t1 = docXml.createElementNS(ns, "w:t");
    t1.textContent = tab;
    r1.appendChild(t1);
    p1.appendChild(r1);
    tc1.appendChild(p1);
    tr.appendChild(tc1);

    const tc2 = docXml.createElementNS(ns, "w:tc");
    files.forEach(fileName => {
      const p = docXml.createElementNS(ns, "w:p");
      const r = docXml.createElementNS(ns, "w:r");
      const t = docXml.createElementNS(ns, "w:t");
      t.textContent = `• ${fileName}`;
      r.appendChild(t);
      p.appendChild(r);
      tc2.appendChild(p);
    });
    tr.appendChild(tc2);
    tbl.appendChild(tr);
  });

  return tbl;
};

export const processArchive = async (file, onProgress, onLog) => {
  try {
    onLog("Loading main ZIP file...");
    const mainZip = await JSZip.loadAsync(file);
    const allPaths = Object.keys(mainZip.files).filter(p => !mainZip.files[p].dir && !p.includes('__MACOSX'));
    
    onLog("Mapping document hierarchy...");
    const { globalMapping: fileToTabMap, appendixIndexToName } = await generateGlobalTabMap(mainZip, allPaths, onLog);
    
    const docxFiles = allPaths.filter(f => f.toLowerCase().endsWith('.docx') && !f.includes('~$')).sort();
    
    for (let i = 0; i < docxFiles.length; i++) {
      const docxPath = docxFiles[i];
      const docName = docxPath.split('/').pop();
      const parts = docxPath.split('/');
      
      // Source appendix number: 
      // If root file "2 Architectural.docx", source number is 2.
      // If folder file "Appendix 2/...", source number is 2.
      const sourceAppNum = getAppNumber(parts[0]);

      onLog(`Processing references in: ${docName}`);
      onProgress(10 + ((i / docxFiles.length) * 40));

      const docxData = await mainZip.file(docxPath).async("arraybuffer");
      const docxZip = await JSZip.loadAsync(docxData);
      if (!docxZip.file("word/document.xml")) continue;

      const relsContent = await docxZip.file("word/_rels/document.xml.rels")?.async("text") || "";
      const parser = new DOMParser();
      const relsDoc = parser.parseFromString(relsContent, "text/xml");
      const linkIdMap = {}; 
      Array.from(relsDoc.getElementsByTagName("Relationship")).forEach(rel => {
        if (rel.getAttribute("TargetMode") === "External") linkIdMap[rel.getAttribute("Id")] = decodeURIComponent(rel.getAttribute("Target"));
      });

      const docContent = await docxZip.file("word/document.xml").async("text");
      const docXml = parser.parseFromString(docContent, "text/xml");
      const ns = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

      // 1. Clean format tags
      Array.from(docXml.getElementsByTagName("w:t")).forEach(t => {
        t.textContent = t.textContent.replace(/\[PDF Format\]/gi, "").replace(/\[DWG Format\]/gi, "");
      });

      // 2. Process Paragraphs
      const paragraphs = Array.from(docXml.getElementsByTagName("w:p"));
      for (const p of paragraphs) {
        const links = Array.from(p.getElementsByTagName("w:hyperlink"));
        const addedRefsInParagraph = new Set();

        for (const link of links) {
          const rId = link.getAttribute("r:id");
          const target = linkIdMap[rId];
          
          if (target && !target.match(/^(https?|mailto):/i)) {
            const cleanTarget = target.replace(/^(\.\.\/)+/, '').replace(/\\/g, '/');
            const targetEntry = Object.entries(fileToTabMap).find(([path]) => path.endsWith(cleanTarget));
            
            if (targetEntry) {
              const [targetPath, mapping] = targetEntry;
              const targetAppFolder = targetPath.split('/')[0];
              const targetAppNum = getAppNumber(targetAppFolder);
              
              let refString = "";
              
              // logic: Check if source context matches target context
              if (sourceAppNum && targetAppNum && sourceAppNum === targetAppNum) {
                // Internal: [See TAB 01]
                refString = ` [See ${mapping.tabName.toUpperCase()}]`;
              } else if (targetAppNum) {
                // Cross-Ref: [See Architectural - Appendix 2 TAB 06]
                const masterName = appendixIndexToName[targetAppNum];
                const prefix = masterName ? `${masterName} - ` : "";
                refString = ` [See ${prefix}${targetAppFolder} ${mapping.tabName.toUpperCase()}]`;
              }

              if (refString && !addedRefsInParagraph.has(refString)) {
                const wR = docXml.createElementNS(ns, "w:r");
                const wRPr = docXml.createElementNS(ns, "w:rPr");
                const wB = docXml.createElementNS(ns, "w:b");
                const wColor = docXml.createElementNS(ns, "w:color");
                wColor.setAttribute("w:val", "000000");
                wRPr.appendChild(wB); wRPr.appendChild(wColor);
                
                const wT = docXml.createElementNS(ns, "w:t");
                wT.setAttribute("xml:space", "preserve");
                wT.textContent = refString;
                wR.appendChild(wRPr); wR.appendChild(wT);
                
                p.insertBefore(wR, link);
                addedRefsInParagraph.add(refString);
              }

              // Normal text conversion
              if (!link.textContent.toLowerCase().includes('click')) {
                Array.from(link.getElementsByTagName("w:r")).forEach(r => {
                  let rPr = r.getElementsByTagName("w:rPr")[0] || docXml.createElementNS(ns, "w:rPr");
                  if (!r.getElementsByTagName("w:rPr")[0]) r.insertBefore(rPr, r.firstChild);
                  const u = rPr.getElementsByTagName("w:u")[0] || rPr.getElementsByTagName("w:rStyle")[0];
                  if (u) rPr.removeChild(u);
                  let c = rPr.getElementsByTagName("w:color")[0] || docXml.createElementNS(ns, "w:color");
                  if (!rPr.getElementsByTagName("w:color")[0]) rPr.appendChild(c);
                  c.setAttribute("w:val", "000000");
                });
                while (link.firstChild) p.insertBefore(link.firstChild, link);
              }
              p.removeChild(link);
            }
          }
        }
      }

      // 3. Add Index Table to Appendix Master Docs
      if (parts[0].toLowerCase().startsWith('appendix')) {
        const body = docXml.getElementsByTagName("w:body")[0];
        const currentAppendixFolder = parts[0];
        
        if (body) {
          const pBreak = docXml.createElementNS(ns, "w:p");
          const rBreak = docXml.createElementNS(ns, "w:r");
          rBreak.appendChild(docXml.createElementNS(ns, "w:br")).setAttribute("w:type", "page");
          pBreak.appendChild(rBreak);
          body.appendChild(pBreak);

          const pTitle = docXml.createElementNS(ns, "w:p");
          const rTitle = docXml.createElementNS(ns, "w:r");
          const rPrTitle = docXml.createElementNS(ns, "w:rPr");
          const sz = docXml.createElementNS(ns, "w:sz"); sz.setAttribute("w:val", "32");
          rPrTitle.appendChild(docXml.createElementNS(ns, "w:b"));
          rPrTitle.appendChild(sz);
          const tTitle = docXml.createElementNS(ns, "w:t");
          tTitle.textContent = `${currentAppendixFolder.toUpperCase()} - PRINT INDEX`;
          rTitle.appendChild(rPrTitle); rTitle.appendChild(tTitle);
          pTitle.appendChild(rTitle);
          body.appendChild(pTitle);

          const indexTable = createWordTable(docXml, ns, currentAppendixFolder, fileToTabMap);
          body.appendChild(indexTable);
        }
      }

      const serializer = new XMLSerializer();
      docxZip.file("word/document.xml", serializer.serializeToString(docXml));
      const newDocxData = await docxZip.generateAsync({ type: "arraybuffer", compression: "DEFLATE" });
      mainZip.file(docxPath, newDocxData);
    }

    // Physical Restructure
    onLog("Finalizing physical structure...");
    onProgress(70);
    const finalManifest = {}; 
    for (const oldPath of allPaths) {
      const mapping = fileToTabMap[oldPath];
      if (mapping) {
        const parts = oldPath.split('/');
        const appendix = parts[0];
        const filename = parts[parts.length - 1];
        const newPath = `${appendix}/${mapping.tabName}/${filename}`;
        const fileBytes = await mainZip.file(oldPath).async("uint8array");
        mainZip.file(newPath, fileBytes);
        mainZip.remove(oldPath);
        if (!finalManifest[appendix]) finalManifest[appendix] = {};
        if (!finalManifest[appendix][mapping.tabName]) finalManifest[appendix][mapping.tabName] = { original: mapping.originalPath, files: [], totalPages: 0 };
        finalManifest[appendix][mapping.tabName].files.push({ name: filename, pages: mapping.pages });
        finalManifest[appendix][mapping.tabName].totalPages += mapping.pages;
      }
    }

    // Master Index HTML
    onLog("Generating Master Print Index...");
    let indexHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Master Print Index</title><style>
      body { font-family: -apple-system, sans-serif; padding: 40px; background: #f8fafc; color: #1e293b; line-height: 1.5; }
      .container { max-width: 1100px; margin: 0 auto; background: white; padding: 40px; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
      h1 { color: #131925; border-bottom: 2px solid #f1f5f9; padding-bottom: 20px; font-size: 2rem; margin-top: 0; }
      .appendix-section { margin-top: 40px; }
      .appendix-title { font-size: 1.5rem; font-weight: 800; color: #131925; background: #bfb582; padding: 10px 20px; border-radius: 8px; margin-bottom: 20px; color: #131925; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; border: 1px solid #e2e8f0; table-layout: fixed; }
      th { background: #f8fafc; text-align: left; padding: 12px 16px; border-bottom: 2px solid #e2e8f0; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; }
      td { padding: 14px 16px; border-bottom: 1px solid #e2e8f0; vertical-align: top; word-wrap: break-word; }
      .tab-cell { font-weight: 700; color: #131925; width: 100px; font-size: 1.1rem; }
      .file-list { margin: 0; padding: 0; list-style: none; font-size: 0.85rem; }
      .file-item { display: flex; justify-content: justify-between; gap: 8px; color: #334155; margin-bottom: 6px; }
      .page-count { color: #94a3b8; font-size: 0.75rem; margin-left: auto; white-space: nowrap; }
    </style></head><body><div class="container"><h1>Master Print Index</h1>`;
    Object.keys(finalManifest).sort().forEach(app => {
      indexHtml += `<div class="appendix-section"><div class="appendix-title">${app}</div><table><thead><tr><th style="width:100px">Tab</th><th>Contents</th><th style="width:80px">Pages</th></tr></thead><tbody>`;
      Object.keys(finalManifest[app]).sort().forEach(tab => {
        const data = finalManifest[app][tab];
        indexHtml += `<tr><td class="tab-cell">${tab}</td><td><ul class="file-list">${data.files.map(f => `<li class="file-item">• ${f.name} <span class="page-count">${f.pages} pg</span></li>`).join('')}</ul></td><td style="font-weight:600;text-align:right">${data.totalPages}</td></tr>`;
      });
      indexHtml += `</tbody></table></div>`;
    });
    indexHtml += `</div></body></html>`;
    mainZip.file("00_MASTER_PRINT_INDEX.html", indexHtml);

    onLog("Generating ZIP archive...");
    const finalZipData = await mainZip.generateAsync({ type: "blob", compression: "DEFLATE" });
    saveAs(finalZipData, `${file.name.replace(/\.zip$/i, '')}_PRINT_PREPARED.zip`);
    onProgress(100);
    onLog("Success: References generated using Root Master Documents.");
    return true;
  } catch (error) {
    onLog(`ERROR: ${error.message}`);
    throw error;
  }
};